/**
 * The case-detail contract — the superset a matter's detail panel reads, plus a
 * mapper per book that projects that book's card data into it.
 *
 * The panel mirrors the Case Portal's detail pane (ADR-0067), brought onto
 * The Surface so an operator reads a matter's full picture without leaving
 * Command Center. Two deliberate cuts:
 *   - **Single-actor view.** Command Center is the internal watch surface, so
 *     the viewer is always the privileged LF-admin operator — none of the
 *     portal's HF/OF contact-masking or firm-name gating is ported (ADR-0068).
 *   - **Read + the operator action set.** Accept / decline / pull on the firm's
 *     behalf are the writes (ADR-0064); the firm-native writes (monthly-update,
 *     FSA upload, settlement, drop) stay in the portal where firms self-file.
 *
 * Every field past `name` is optional: a book maps in what its card already
 * carries, and the panel renders a section only when its field is present. So a
 * thin terminal card yields a thin panel and a rich circulating lead a rich one
 * — and the blanks are the map of the data work still to do.
 *
 * Slice 4 (portal→CC merge, ADR-0067) added the net-new sections: next-filing
 * deadline, FSA status, the unified timeline (merging routing history with
 * monthly-update entries), plus the monthly-update cadence read. The class-B
 * placement fields that back these — `nextFilingDeadline`,
 * `nextFilingDeadlineType`, `lastMonthlyUpdateDate`, `monthlyUpdates` — now
 * project through the Placed book / Settled book / Closed / Dropped books.
 */

import type {
  CaseDocument,
  CaseLookupResponse,
  ClosedCase,
  DroppedCase,
  AcceptedLead,
  PlacedCase,
  SettledCase,
  Lead,
  LeadAttachment,
  MonthlyUpdateEntry,
  RejectedCase,
  UnderReviewLead,
  UnderReviewMisc,
} from "@command-center/shared";

/** One firm's leg of a matter's routing journey — for the Routing history
 *  section. The decline reason is the evidence; shown, not scored. */
export type CaseDetailRoutingLeg = {
  firmName: string | null;
  decision: string | null;
  dateSent: string | null;
  declineReason: string | null;
};

/** The lead an operator-level action targets (slice 5c: reject + edit-client).
 *  Parallel to `CaseDetailActionTarget` but for **lead-side** writes — the two
 *  actions that mutate the Lead record itself rather than a Placement under it.
 *
 *  `leadId` is the Airtable record ID for the wire calls. `leadStatus` is the
 *  current Lead Status (one of the 11 values) — used by the panel to gate the
 *  Reject button (`canReject` is false for terminal statuses). When the book
 *  doesn't know the live status, leave it null — the panel reads that as "no
 *  Reject button," which is safe (the route enforces the same gate
 *  server-side).
 *
 *  Absent on terminal books that don't expose lead-edits in this slice
 *  (Dropped / Closed / Rejected — deferred to a later polish pass per the
 *  slice-5c scope cut). */
export type CaseDetailLeadActionTarget = {
  leadId: string;
  leadStatus: string | null;
  /**
   * Slice 5d — TRUE when the lead is a signed Transfer. The Route action
   * (Pending → Under Review with a manually picked HF) is the manual
   * companion to the n8n auto-router, which handles unsigned leads only;
   * only a signed transfer can be manually routed. Null when the book
   * doesn't project signed (treated as false by the footer — same posture
   * as null `leadStatus` gating Reject off).
   */
  signed?: boolean | null;
  /**
   * Slice 5d — the lead's human-readable Lead ID (the primary text, e.g.
   * `KennethSlack`). Carried so the firm-picker modal can render a label
   * a reader will recognise without re-fetching the lead. Optional —
   * the route handler is the source of truth for the ID; this is display
   * polish.
   */
  leadHumanId?: string | null;
};

/** The placement an operator action targets, when the book exposes one. Carries
 *  the record id to write and the placement's current decision so the panel can
 *  gate which actions are valid (accept/decline/pull bands). Absent on books
 *  with no actionable placement (limbo, terminal, lead-only).
 *
 *  Slice 5b extends this with the settlement-band signals the footer needs to
 *  gate Confirm LF/OF Payment + Edit Settlement button visibility (so the panel
 *  never offers an action the server will 409). All five are optional and
 *  default to null — books outside the Settled band (Placed book at Placed)
 *  surface a footer with only Close Case available; lead-side bands ignore them. */
export type CaseDetailActionTarget = {
  placementId: string;
  decision: string | null;
  /**
   * Slice 5d — TRUE when the linked lead is signed. Reroute applies only
   * to Declined/Pulled placements on signed transfers (the n8n auto-router
   * handles unsigned reroutes); the footer hides the Reroute button when
   * this is false or null. Default null on books where the linked-lead
   * signed bit isn't projected — safe (no button).
   */
  linkedLeadSigned?: boolean | null;
  /** ISO date (YYYY-MM-DD) LF received its disbursement share, or null. */
  lfPaymentDate?: string | null;
  /** ISO date (YYYY-MM-DD) OF received its share, or null. */
  ofPaymentDate?: string | null;
  /** Current settlement amount (currency → number) — for prefilling the
   *  edit-settlement form. */
  settlementAmount?: number | null;
  /** Current attorney fee rate as a decimal (0.33 = 33%) — for prefilling
   *  the edit-settlement form. */
  attorneyFeeRate?: number | null;
  /** Current settlement date (YYYY-MM-DD) — for prefilling the
   *  edit-settlement form. */
  settlementDate?: string | null;
};

/** The fee split + amount for the Settlement section. Fee shares are decimals
 *  (0.6 = 60%). When LegalFlare is itself the OF the split is two-party — the
 *  panel drops the separate LF line. */
export type CaseDetailSettlement = {
  amount: number | null;
  hfFeePct: number | null;
  lfFeePct: number | null;
  ofFeePct: number | null;
  isLegalFlareOf: boolean;
};

/**
 * FSA section read. Derived per book from the existing signals — there is no
 * dedicated FSA Document field on Placement (the FSA upload is what flips a
 * placement to **Placed** and stamps `placedDate`, so submission IS implicit
 * in being past Accepted). The shape stays uniform so the panel doesn't
 * branch per book:
 *   - Accepted matters (Accepted book) → `outstanding`, date = the accepted moment
 *     (the FSA wait clock).
 *   - Placed / Settled / Closed / Dropped → `submitted`, date = placedDate
 *     where the book exposes it (Placed book does; the others currently
 *     don't, so the panel just reads "submitted" without a date).
 */
export type CaseDetailFsa = {
  status: "outstanding" | "submitted";
  date: string | null;
  /** The client-signed FSA document (filename + url) when on file, for the
   *  panel's read-only download. Absent/null when none is attached — e.g. an
   *  Accepted matter still awaiting the FSA (status "outstanding"), or a
   *  placed matter whose upload predates the field being read. */
  document?: CaseDocument | null;
};

/**
 * One event on the merged timeline (slice 4). The timeline weaves routing
 * milestones together with monthly-update entries so the operator reads a
 * matter's chronological story in one place rather than scanning two
 * sections separately. The panel sorts newest-first.
 *
 *   - `routing` events come from `UnderReviewLead.trail` (or the routing
 *     history a future detail endpoint adds for non-Under Review books).
 *   - `monthly-update` events come from the parsed Monthly Updates log
 *     on the placement (`monthlyUpdates` on the post-placed books).
 *
 * Outreach-log entries are NOT on the timeline this slice — they're
 * partially summarised already (Under Review book / Accepted book surface
 * `lastOutreachDays`), and adding individual entries means parsing the
 * raw `outreachLog` multilineText, which is a slice-6 polish item.
 */
export type CaseDetailTimelineEvent = {
  /** ISO date or datetime — anchor for chronological sort + display. */
  date: string;
  kind: "routing" | "monthly-update" | "note";
  /** One-line headline (firm name + decision, or update outcome). */
  label: string;
  /** Optional secondary line — decline reason, update note, etc. */
  detail: string | null;
};

/**
 * Weave a matter's Case Notes (#692) into its timeline.
 *
 * WHY NOTES BELONG ON THE TIMELINE AND NOT IN THEIR OWN SECTION. A matter has
 * one history. Command Center records the transitions the system caused; a note
 * records what a person did about them — a call, a decision to hold rather than
 * reroute. Read apart, neither answers "what happened on this case"; read
 * together they do, which is the whole point of the merge the timeline already
 * performs for monthly updates.
 *
 * Notes are anchored by `occurredOn` (when the thing happened), not by when
 * they were written — otherwise a note written up three days late would sort
 * beside events it had nothing to do with.
 *
 * `firmNameFor` maps a note's placement to a firm name so the entry says WHICH
 * firm it concerns; a matter can have been sent to three, and "Note" alone
 * would leave the reader guessing.
 */
export function weaveNotesIntoTimeline(
  events: CaseDetailTimelineEvent[],
  notes: {
    occurredOn: string;
    author: string;
    body: string;
    placementRecordId: string | null;
  }[],
  firmNameFor: (placementId: string) => string | null,
): CaseDetailTimelineEvent[] {
  const noteEvents: CaseDetailTimelineEvent[] = notes.map((n) => {
    const firm =
      n.placementRecordId !== null ? firmNameFor(n.placementRecordId) : null;
    // The author's local part: "chris.rose" reads at a glance where the full
    // address would crowd the line, and every author is an LF operator.
    const who = n.author.split("@")[0] ?? n.author;
    return {
      date: n.occurredOn,
      kind: "note" as const,
      label: firm ? `Note · ${firm} · ${who}` : `Note · ${who}`,
      detail: n.body,
    };
  });
  // Newest first, matching what the panel renders for the rest of the timeline.
  return [...events, ...noteEvents].sort((a, b) => b.date.localeCompare(a.date));
}

export type CaseDetail = {
  /** Client / matter name — the one always-present field. */
  name: string;
  /** The matter's LEAD Airtable record id — keys lead-scoped panel sections
   *  (firm messages, #542). Distinct from `leadActionTarget` (which also
   *  gates actions): this is pure identity, set by every mapper that has the
   *  id. Null/absent = the section doesn't render (e.g. Rejected's book card
   *  carries no record id). */
  leadRecordId?: string | null;
  status?: string | null;
  /** TRUE for a Transfer (already-retained client). */
  signed?: boolean;
  /** The matter's Creation Date (YYYY-MM-DD) — rendered as "Received {date}"
   *  in the panel header. Null/absent when the book doesn't carry it. */
  receivedDate?: string | null;
  /** Canonical lifecycle stage — drives the timeline's stage-aware prompt and
   *  the filing-deadline empty-state gate. Distinct from `status` (the header
   *  badge): an Accepted book matter's `status` is its lead status while `stage` is
   *  "Accepted". Absent when the book doesn't set it. */
  stage?: string | null;
  caseType?: string | null;
  incidentState?: string | null;
  originatingFirmName?: string | null;
  handlingFirmName?: string | null;
  phone?: string | null;
  email?: string | null;
  caseSummary?: string | null;
  caseFile?: LeadAttachment[];
  declineReason?: string | null;
  dropReason?: string | null;
  settlement?: CaseDetailSettlement | null;
  routing?: CaseDetailRoutingLeg[];
  /** The placement an operator action targets, when this book exposes one. */
  actionTarget?: CaseDetailActionTarget | null;
  /** The lead a lead-level action targets (slice 5c — reject + edit-client).
   *  Independent of `actionTarget`: terminal-placement books still expose
   *  this so the operator can edit client info, while limbo-lead books may
   *  expose this without a placement target. */
  leadActionTarget?: CaseDetailLeadActionTarget | null;

  // ----- slice 4 (portal→CC merge panel upgrade, ADR-0067) -----
  /** Next hard-cliff filing deadline (ISO date), null when unset or N/A. */
  nextFilingDeadline?: string | null;
  /** Singleselect label naming the kind of deadline. */
  nextFilingDeadlineType?: string | null;
  /** FSA submission read — derived from decision + placedDate per book. */
  fsa?: CaseDetailFsa | null;
  /** ISO date the HF last filed a monthly update, or null. */
  lastMonthlyUpdateDate?: string | null;
  /** Parsed Monthly Update entries (newest first). Empty when the
   *  book / placement carries none. */
  monthlyUpdates?: MonthlyUpdateEntry[];
  /** Merged routing + monthly-update timeline, newest first. */
  timeline?: CaseDetailTimelineEvent[];
};

// ---------------------------------------------------------------------------
// Helpers — derive timeline + FSA from each book's available signals.
// ---------------------------------------------------------------------------

/** Build timeline events from a routing trail (Under Review book's full multi-firm
 *  history). Anchored on `dateSent` because that's the moment of the leg. */
function timelineFromTrail(
  trail: UnderReviewLead["trail"],
): CaseDetailTimelineEvent[] {
  return trail
    .filter((leg) => leg.dateSent !== null)
    .map<CaseDetailTimelineEvent>((leg) => ({
      date: leg.dateSent as string,
      kind: "routing",
      label: leg.decision
        ? `${leg.firmName ?? "Unknown firm"} — ${leg.decision}`
        : (leg.firmName ?? "Unknown firm"),
      detail: leg.declineReason,
    }));
}

/** Build timeline events from parsed monthly-update entries. */
function timelineFromMonthly(
  updates: MonthlyUpdateEntry[],
): CaseDetailTimelineEvent[] {
  return updates.map<CaseDetailTimelineEvent>((u) => ({
    date: u.date,
    kind: "monthly-update",
    label: u.outcome ?? "Monthly update",
    detail: u.note,
  }));
}

/** Merge timeline streams, newest first. Stable on equal dates. */
function mergeTimeline(
  ...streams: CaseDetailTimelineEvent[][]
): CaseDetailTimelineEvent[] {
  return streams
    .flat()
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
}

// ---------------------------------------------------------------------------
// Per-book mappers
// ---------------------------------------------------------------------------

/** Under Review book circulating lead — the richest source: contact, file, summary, and
 *  the full firm trail (each leg's decline reason rides into Routing history
 *  AND seeds the unified timeline). Pre-placed — no filing deadline / monthly
 *  updates / FSA-submitted state. */
export function underReviewLeadToDetail(lead: UnderReviewLead): CaseDetail {
  const trailLegs: CaseDetailRoutingLeg[] = lead.trail.map((p) => ({
    firmName: p.firmName,
    decision: p.decision,
    dateSent: p.dateSent,
    declineReason: p.declineReason,
  }));
  return {
    name: lead.name,
    leadRecordId: lead.id,
    status: lead.status,
    signed: lead.signed,
    caseType: lead.caseType,
    incidentState: lead.incidentState,
    originatingFirmName: lead.originatingFirmName,
    handlingFirmName: lead.currentFirmName,
    phone: lead.phone,
    email: lead.email,
    caseSummary: lead.caseSummary,
    caseFile: lead.intakeDocs,
    receivedDate: lead.creationDate,
    stage: lead.status,
    routing: trailLegs,
    timeline: timelineFromTrail(lead.trail),
    actionTarget: {
      placementId: lead.currentPlacementId,
      decision: lead.currentDecision,
      // Slice 5d — Reroute gates on Declined/Pulled placements of SIGNED
      // transfers. The Under Review lead carries its own `signed` bit; project
      // it onto the placement-side target so the footer can show Reroute
      // when the current placement came back Declined/Pulled.
      linkedLeadSigned: lead.signed,
    },
    // Slice 5c — lead-side actions (reject + edit-client). A circulating
    // Under Review lead is exactly where reject is highest-value (a dead-on-
    // arrival lead clogs routing) and where edit-client corrections are
    // most actionable (contact / state fixes feed back into routing).
    // Slice 5d — `signed` + `leadHumanId` enable the Route action's footer
    // gate + the firm-picker modal's display label.
    leadActionTarget: {
      leadId: lead.id,
      leadStatus: lead.status,
      signed: lead.signed,
      leadHumanId: lead.leadId,
    },
  };
}

/** Under Review miscellaneous (limbo statuses) — no routing trail, no contact on
 *  the card; just identity + the blocker status + the summary. */
export function underReviewMiscToDetail(misc: UnderReviewMisc): CaseDetail {
  return {
    name: misc.name,
    leadRecordId: misc.id,
    status: misc.status,
    signed: misc.signed,
    caseType: misc.caseType,
    incidentState: misc.incidentState,
    originatingFirmName: misc.originatingFirmName,
    caseSummary: misc.caseSummary,
    receivedDate: misc.receivedDate,
    stage: misc.status,
    // Slice 5c — limbo leads (Unreviewed / Unmatched / Incomplete / Pending /
    // Reroute / Re-present) are prime reject candidates: routing can't move
    // them, and an edit-client fix may unstick them on the next routing pass
    // (the incidentState side effect at the route is designed for exactly
    // this case).
    // Slice 5d — a Pending + signed misc lead is the canonical Route candidate.
    // The route action surfaces here (and only here, among the Under Review book
    // groups) when `canRoute(status) && signed === true`.
    leadActionTarget: {
      leadId: misc.id,
      leadStatus: misc.status,
      signed: misc.signed,
      leadHumanId: misc.leadId,
    },
  };
}

/** Accepted book (Accepted, awaiting FSA) — lead-based, so contact + file + summary,
 *  committed to one firm. The defining FSA-outstanding signal anchors on
 *  `acceptedDate`. Pre-placed: no filing deadline / monthly updates yet. */
export function acceptedToDetail(lead: AcceptedLead): CaseDetail {
  return {
    name: lead.name,
    leadRecordId: lead.id,
    status: lead.status,
    signed: lead.signed,
    caseType: lead.caseType,
    incidentState: lead.incidentState,
    originatingFirmName: lead.originatingFirmName,
    handlingFirmName: lead.currentFirmName,
    phone: lead.phone,
    email: lead.email,
    caseSummary: lead.caseSummary,
    caseFile: lead.intakeDocs,
    receivedDate: lead.creationDate,
    stage: "Accepted",
    fsa: { status: "outstanding", date: lead.acceptedDate },
    actionTarget: {
      placementId: lead.acceptedPlacementId,
      decision: "Accepted",
    },
    // Slice 5c — an accepted-but-stalled lead is still pre-terminal; reject
    // surfaces here for the rare dead-on-arrival case that slipped through,
    // and edit-client surfaces for contact corrections (frequent during the
    // FSA chase).
    leadActionTarget: { leadId: lead.id, leadStatus: lead.status },
  };
}

/** Placed book (Placed census) — the richest *post-placed* mapper: filing
 *  deadline + type, FSA submitted with the actual placedDate, monthly-update
 *  cadence + parsed entries, timeline from monthly updates. The actionTarget
 *  exposes Close Case from this book (slice 5a) — by definition every Placed
 *  case in the census is unclosed (Closed cases have moved to the Closed
 *  book), so caseClosedDate is null here, and the button visibility is just
 *  the CLOSE_GATE check on Decision = "Placed". */
export function placedToDetail(c: PlacedCase): CaseDetail {
  return {
    name: c.client,
    leadRecordId: c.leadRecordId,
    status: "Placed",
    stage: "Placed",
    signed: c.signed,
    receivedDate: c.receivedDate,
    caseType: c.caseType,
    incidentState: c.incidentState,
    originatingFirmName: c.originatingFirmName,
    handlingFirmName: c.handlingFirmName,
    caseSummary: c.caseSummary,
    caseFile: c.caseFile,
    nextFilingDeadline: c.nextFilingDeadline,
    nextFilingDeadlineType: c.nextFilingDeadlineType,
    fsa: { status: "submitted", date: c.placedDate, document: c.fsaDocument },
    lastMonthlyUpdateDate: c.lastMonthlyUpdateDate,
    monthlyUpdates: c.monthlyUpdates,
    timeline: mergeTimeline(timelineFromMonthly(c.monthlyUpdates)),
    actionTarget: {
      placementId: c.id,
      decision: "Placed",
    },
    // Slice 5c — a Placed lead's Status is "Placed" (a TERMINAL_LEAD_STATUSES
    // member), so canReject returns false and the Reject button stays hidden
    // — correct (you don't reject a lead the firm has accepted and placed).
    // Edit-client still surfaces; admin can correct client info during the
    // post-placement work. leadStatus is null here because the book is
    // placement-driven and doesn't project the live lead status; null reads
    // as "no reject button" — exactly the gate we want.
    leadActionTarget: { leadId: c.leadRecordId, leadStatus: null },
  };
}

/** Settled book (Settled worklist) — the fee split + settlement amount,
 *  plus the post-placed read (filing deadline + monthly updates still
 *  live until close). FSA submission date is not exposed on this book's
 *  card, so the FSA section reads "submitted" without a date. */
export function settledToDetail(c: SettledCase): CaseDetail {
  return {
    name: c.client,
    leadRecordId: c.leadRecordId,
    status: "Settled",
    stage: "Settled",
    signed: c.signed,
    receivedDate: c.receivedDate,
    caseFile: c.caseFile,
    originatingFirmName: c.originatingFirmName,
    handlingFirmName: c.handlingFirmName,
    settlement: {
      amount: c.settlementAmount,
      hfFeePct: c.hfFeePct,
      lfFeePct: c.lfFeePct,
      ofFeePct: c.ofFeePct,
      isLegalFlareOf: c.originatingFirmIsLegalFlare,
    },
    nextFilingDeadline: c.nextFilingDeadline,
    nextFilingDeadlineType: c.nextFilingDeadlineType,
    fsa: { status: "submitted", date: null, document: c.fsaDocument },
    lastMonthlyUpdateDate: c.lastMonthlyUpdateDate,
    monthlyUpdates: c.monthlyUpdates,
    timeline: mergeTimeline(timelineFromMonthly(c.monthlyUpdates)),
    // Slice 5a: Close Case is valid from Settled — every Settled book row is
    // a Settled-but-not-yet-Closed placement by construction. The route
    // double-checks against the live placement (caseClosedDate === null).
    //
    // Slice 5b extends actionTarget with the settlement-band signals so the
    // footer can gate Confirm LF/OF Payment + Edit Settlement (prefill the
    // form with current values).
    actionTarget: {
      placementId: c.id,
      decision: "Settled",
      lfPaymentDate: c.lfPaymentDate,
      ofPaymentDate: c.ofPaymentDate,
      settlementAmount: c.settlementAmount,
      attorneyFeeRate: c.attorneyFeeRate,
      settlementDate: c.settlementDate,
    },
    // Slice 5c — same call as Placed book: lead's Status is "Placed"
    // (terminal); leadStatus null hides Reject server-side and UI-side.
    // Edit-client remains available for contact corrections during the
    // settlement chase.
    leadActionTarget: { leadId: c.leadRecordId, leadStatus: null },
  };
}

/** Dropped (terminal) — identity, both firms, the drop reason, plus the
 *  historical class-B fields (the filing deadline + monthly-update history
 *  up to the drop). */
export function droppedToDetail(c: DroppedCase): CaseDetail {
  return {
    name: c.client,
    leadRecordId: c.leadRecordId,
    status: "Dropped",
    stage: "Dropped",
    signed: c.signed,
    receivedDate: c.receivedDate,
    caseFile: c.caseFile,
    originatingFirmName: c.originatingFirmName,
    handlingFirmName: c.handlingFirmName,
    dropReason: c.dropReason,
    nextFilingDeadline: c.nextFilingDeadline,
    nextFilingDeadlineType: c.nextFilingDeadlineType,
    fsa: { status: "submitted", date: null, document: c.fsaDocument },
    lastMonthlyUpdateDate: c.lastMonthlyUpdateDate,
    monthlyUpdates: c.monthlyUpdates,
    timeline: mergeTimeline(timelineFromMonthly(c.monthlyUpdates)),
  };
}

/** Closed (terminal graveyard) — identity + both firms + the settlement amount
 *  (no fee split on the card; here the case is done) + the historical
 *  class-B read. */
export function closedToDetail(c: ClosedCase): CaseDetail {
  return {
    name: c.client ?? "Unresolved case",
    leadRecordId: c.leadRecordId,
    status: "Case Closed",
    stage: "Case Closed",
    signed: c.signed,
    receivedDate: c.receivedDate,
    caseFile: c.caseFile,
    originatingFirmName: c.originatingFirmName,
    handlingFirmName: c.handlingFirmName,
    settlement:
      c.settlementAmount !== null
        ? {
            amount: c.settlementAmount,
            hfFeePct: null,
            lfFeePct: null,
            ofFeePct: null,
            isLegalFlareOf: false,
          }
        : null,
    nextFilingDeadline: c.nextFilingDeadline,
    nextFilingDeadlineType: c.nextFilingDeadlineType,
    fsa: { status: "submitted", date: null, document: c.fsaDocument },
    lastMonthlyUpdateDate: c.lastMonthlyUpdateDate,
    monthlyUpdates: c.monthlyUpdates,
    timeline: mergeTimeline(timelineFromMonthly(c.monthlyUpdates)),
  };
}

/** Rejected (terminal, lead-based) — identity, OF, case type/state. No handling
 *  firm: a rejected matter was never placed. Pre-placed signals don't apply. */
export function rejectedToDetail(c: RejectedCase): CaseDetail {
  return {
    name: c.client ?? "Unnamed lead",
    leadRecordId: c.id,
    caseType: c.caseType,
    incidentState: c.caseState,
    originatingFirmName: c.originatingFirmName,
    receivedDate: c.createdDate,
    stage: "Rejected",
  };
}

/** Fallback (a matter no book claimed) — a base Lead has contact + file +
 *  summary but no joined firm names. */
export function leadToDetail(lead: Lead): CaseDetail {
  return {
    name: lead.name,
    leadRecordId: lead.id,
    status: lead.status,
    signed: lead.signed,
    caseType: lead.caseType,
    incidentState: lead.incidentState,
    phone: lead.phone,
    email: lead.email,
    caseSummary: lead.caseSummary,
    caseFile: lead.intakeDocs,
    receivedDate: lead.creationDate,
    stage: lead.status,
  };
}

/** Global-search lookup (#434) — the by-id read behind the Find-any-case panel.
 *  Maps the enriched lookup payload into the case-detail superset; the routing
 *  trail carries the firm story (e.g. Protection declined → Coastline declined).
 *  Settlement / filing-deadline / monthly-update sections are absent by design
 *  (the lookup doesn't carry them — the panel renders only present sections). */
export function caseLookupToDetail(r: CaseLookupResponse): CaseDetail {
  return {
    name: r.name,
    leadRecordId: r.leadRecordId,
    status: r.status,
    stage: r.status,
    signed: r.signed,
    caseType: r.caseType,
    incidentState: r.incidentState,
    originatingFirmName: r.originatingFirmName,
    phone: r.phone,
    email: r.email,
    caseSummary: r.caseSummary,
    routing: r.routing,
  };
}
